/**
 * Platform Admin — обзорный экран.
 *
 * Цифры целиком приходят с сервера (`/platform-admin/dashboard/kpis`).
 * Фикстуры убраны (AK-470): чего сервер не считает — приростов за 30 дней,
 * выручки, статуса и числа пользователей в топ-10 — того здесь и нет.
 * Прочерк с подписью «нет данных» честнее правдоподобной выдумки: по
 * выдуманной выручке founder примет решение, по прочерку — заведёт задачу.
 */
import { RefreshCw, AlertCircle, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { PlatformAdminLayout } from '@/layouts/PlatformAdminLayout';
import { usePlatformDashboardKpis } from './hooks/usePlatformAdminQueries';
import { formatHectares, formatNumber, formatRub } from './format';
import { cn } from '@/lib/utils';

/** Ячейка/плитка без источника данных. */
const NO_DATA = '—';

interface KpiCardProps {
  label: string;
  value: string;
  /** Подпись под числом. Не «прирост» — сервер приростов не считает. */
  hint?: string;
  /** Приглушить подпись: значение неизвестно, а не «плохое». */
  hintMuted?: boolean;
  /**
   * AK-574 — плитка считает не всё, что обещает заголовком: часть тенантов
   * не просуммировалась. Янтарь + текст, а не молчание: неполная сумма,
   * выданная за полную, хуже отсутствия цифры.
   */
  warning?: string;
}

function KpiCard({ label, value, hint, hintMuted, warning }: KpiCardProps) {
  return (
    <Card>
      <CardContent className="px-4 py-3">
        <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
          {label}
        </div>
        <div className="mt-1 text-[26px] font-semibold leading-none text-foreground">
          {value}
        </div>
        {hint && (
          <div
            className={cn(
              'mt-1.5 text-xs',
              hintMuted ? 'text-muted-foreground' : 'text-primary',
            )}
          >
            {hint}
          </div>
        )}
        {warning && (
          <div className="mt-2 flex items-start gap-1 text-xs leading-4 text-accent-brand">
            <AlertTriangle className="h-3 w-3 shrink-0" />
            <span>{warning}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/** «1 хозяйство» / «2 хозяйства» / «5 хозяйств» / «12 хозяйств». */
export function pluralTenants(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 14) return 'хозяйств';
  if (mod10 === 1) return 'хозяйство';
  if (mod10 >= 2 && mod10 <= 4) return 'хозяйства';
  return 'хозяйств';
}

function KpiSkeleton() {
  return (
    <Card>
      <CardContent className="px-4 py-3">
        <Skeleton className="h-3 w-20" />
        <Skeleton className="mt-2 h-7 w-24" />
        <Skeleton className="mt-2 h-3 w-16" />
      </CardContent>
    </Card>
  );
}

export function PlatformAdminDashboardPage() {
  const { data, isLoading, isError, error, refetch, isFetching, dataUpdatedAt } =
    usePlatformDashboardKpis();

  // Время берём с момента загрузки на клиенте: сервер отметку снимка не
  // отдаёт, а прежняя `generated_at` была частью фикстуры.
  const lastUpdated =
    data && dataUpdatedAt
      ? new Date(dataUpdatedAt).toLocaleTimeString('ru-RU', {
          hour: '2-digit',
          minute: '2-digit',
        })
      : null;

  // Доля активных — единственная производная величина: считается из двух
  // живых чисел, ничего не досочиняет.
  const activePct =
    data && data.tenants_total > 0
      ? Math.round((data.tenants_active / data.tenants_total) * 100)
      : null;

  return (
    <PlatformAdminLayout>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Платформа · обзор</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {lastUpdated
              ? `Снимок по всем tenants · обновлён в ${lastUpdated}`
              : 'Снимок по всем tenants'}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => refetch()}
          disabled={isFetching}
        >
          <RefreshCw className={cn('h-3.5 w-3.5', isFetching && 'animate-spin')} />
          Обновить
        </Button>
      </div>

      {isError && (
        <div className="mt-5 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <div className="flex-1">
            <div className="font-medium">Не удалось загрузить KPI</div>
            <div className="text-xs opacity-80">{error?.message ?? 'Неизвестная ошибка'}</div>
          </div>
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            Повторить
          </Button>
        </div>
      )}

      {/* KPI grid 5×1 на md+; на мобиле — 2 в строку */}
      <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-5">
        {isLoading && !data
          ? Array.from({ length: 5 }).map((_, i) => <KpiSkeleton key={i} />)
          : data && (
              <>
                <KpiCard
                  label="Компаний всего"
                  value={formatNumber(data.tenants_total)}
                  hint={
                    data.tenants_archived > 0
                      ? `включая ${formatNumber(data.tenants_archived)} в архиве`
                      : 'включая архив'
                  }
                  hintMuted
                />
                <KpiCard
                  label="Активных"
                  value={formatNumber(data.tenants_active)}
                  // AK-574 — подписываем, что именно считается: до этого KPI
                  // считал по легаси `tenants.status`, а список рисовал бейдж
                  // по `access_state`, и две цифры про одно расходились.
                  hint={
                    activePct === null
                      ? 'доступ «активна»'
                      : `${activePct}% · доступ «активна»`
                  }
                />
                <KpiCard
                  label="Пользователи 30д"
                  value={formatNumber(data.users_active_30d)}
                />
                <KpiCard
                  label="Площадь, га"
                  value={formatNumber(Math.round(data.area_ha_total))}
                  warning={
                    data.area_skipped_tenants > 0
                      ? `не учтено ${formatNumber(data.area_skipped_tenants)} ${pluralTenants(
                          data.area_skipped_tenants,
                        )}: схема ещё не развёрнута`
                      : undefined
                  }
                />
                <KpiCard
                  label="Выручка 30д, ₽"
                  value={
                    data.revenue_rub_30d == null
                      ? NO_DATA
                      : formatRub(data.revenue_rub_30d)
                  }
                  hint={data.revenue_rub_30d == null ? 'нет данных' : undefined}
                  hintMuted
                />
              </>
            )}
      </div>

      {/* Top-10 by area */}
      <Card className="mt-6">
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <h2 className="text-base font-semibold text-foreground">
            Топ-10 tenants по площади
          </h2>
          <a
            href="/platform-admin/companies"
            className="text-sm font-medium text-primary hover:underline"
          >
            Все компании →
          </a>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Компания</TableHead>
                <TableHead>Slug</TableHead>
                <TableHead>Статус</TableHead>
                <TableHead className="text-right">Юзеры</TableHead>
                <TableHead className="text-right">Площадь, га</TableHead>
                <TableHead>Платно до</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && !data
                ? Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i}>
                      <TableCell>
                        <Skeleton className="h-4 w-32" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-24" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-14" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="ml-auto h-4 w-10" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="ml-auto h-4 w-16" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-20" />
                      </TableCell>
                    </TableRow>
                  ))
                : data && data.top_10_by_area.length === 0
                  ? (
                      <TableRow>
                        <TableCell
                          colSpan={6}
                          className="py-6 text-center text-sm text-muted-foreground"
                        >
                          Пока ни у одной компании нет полей с площадью
                        </TableCell>
                      </TableRow>
                    )
                  : data?.top_10_by_area.map((t) => (
                      <TableRow key={t.tenant_id}>
                        <TableCell className="font-medium text-foreground">
                          {t.name}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {t.slug}
                        </TableCell>
                        {/* Статус, число пользователей и «платно до» этот
                            эндпоинт не отдаёт — они живут в реестре компаний
                            и в биллинге. Ссылка «Все компании →» ведёт туда. */}
                        <TableCell className="text-muted-foreground">
                          {NO_DATA}
                        </TableCell>
                        <TableCell className="text-right text-muted-foreground">
                          {NO_DATA}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatHectares(t.area_ha).replace(' га', '')}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {NO_DATA}
                        </TableCell>
                      </TableRow>
                    ))}
            </TableBody>
          </Table>
        </div>
      </Card>
    </PlatformAdminLayout>
  );
}

export default PlatformAdminDashboardPage;
